import { getAccessToken } from "../utils/auth";
import React, { useCallback, useEffect, useState } from "react";
import axios from "../utils/api.js";
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";

const formatCurrency = (amount, currency = "INR") =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(amount) || 0);

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString("en-IN", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
};

export default function RefundActivity() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState("month");
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);

  const loadRefunds = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get("/payment-refunds", {
        params: { period, limit: 100 },
        headers: { Authorization: `Bearer ${getAccessToken()}` },
      });
      if (res.data?.success) {
        setRows(Array.isArray(res.data.data) ? res.data.data : []);
        setSummary(res.data.summary || null);
      } else {
        setRows([]);
        setSummary(null);
      }
    } catch (err) {
      console.error("Error loading refund activity:", err);
      setRows([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    loadRefunds();
  }, [loadRefunds]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, bgcolor: "#f8f9fa", minHeight: "100vh" }}>
      <Paper elevation={0} sx={{ p: 4, borderRadius: 4, maxWidth: 1400, mx: "auto" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3, flexWrap: "wrap" }}>
          <IconButton onClick={() => navigate("/subscription-analysis")}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Refund Activity
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Mandate verification refunds and billing refunds across subscription payments
            </Typography>
          </Box>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Period</InputLabel>
            <Select value={period} label="Period" onChange={(e) => setPeriod(e.target.value)}>
              <MenuItem value="day">Today</MenuItem>
              <MenuItem value="week">This week</MenuItem>
              <MenuItem value="month">This month</MenuItem>
              <MenuItem value="year">This year</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {summary ? (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: 3, bgcolor: "#fff7ed" }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Total refunded
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: "#c2410c" }}>
                    {formatCurrency(summary.totalRefunded)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {summary.refundCount || 0} refund(s)
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: 3, bgcolor: "#ecfdf5" }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Autopay verification
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: "#047857" }}>
                    {formatCurrency(summary.mandateAuth?.totalAmount)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {summary.mandateAuth?.count || 0} refund(s)
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Plan / billing refunds
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800 }}>
                    {formatCurrency(summary.other?.totalAmount)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {summary.other?.count || 0} refund(s)
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        ) : null}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Plan / type</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Refund type</TableCell>
                <TableCell>Refund ID</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary" }}>
                    No refunds recorded for this period.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.id || row.razorpay_payment_id} hover>
                    <TableCell>{formatDate(row.refund?.refundedAt || row.updatedAt || row.paymentDate)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {row.userName || "—"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.userEmail || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {row.planName || row.type || "—"}
                      {row.description ? (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {row.description}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(row.finalPrice ?? row.amount, row.currency)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={row.refund?.label || "Refunded"}
                        sx={{
                          bgcolor: row.refund?.isMandateAuthRefund ? "#ecfdf5" : "#fef2f2",
                          color: row.refund?.isMandateAuthRefund ? "#047857" : "#b91c1c",
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                      {row.refund?.refundId || "—"}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
